import React, { useState, useMemo } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';


const GridView = ({ goals, onView, onEdit, onDelete, onStatusChange, language = 'pt' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });


  const getLabels = () => {
    const labels = {
      pt: {
        searchPlaceholder: 'Buscar por título ou responsável...',
        filterByStatus: 'Filtrar por Status',
        filterByArea: 'Filtrar por Área',
        allStatus: 'Todos os Status',
        allAreas: 'Todas as Áreas',
        planned: 'Planejado',
        inProgress: 'Em Progresso',
        withMentor: 'Com Mentoria',
        followUp: 'Acompanhamento',
        statusCompleted: 'Concluído',
        archived: 'Arquivado',
        noGoalsFound: 'Nenhuma meta encontrada',
        status: 'Status',
        goalObjective: 'Meta',
        lifeArea: 'Área',
        currentState: 'Estado Atual',
        futureGoal: 'Alcance (Futuro)',
        budget: 'Orçamento',
        responsible: 'Responsável',
        whoCanHelp: 'Quem?',
        howToDo: 'Como?',
        startDate: 'Quando?',
        targetDate: 'Data Alvo',
        observations: 'Observações',
        measures: 'Medidas',
        actions: 'Ações'
      },
      en: {
        searchPlaceholder: 'Search by title or responsible...',
        filterByStatus: 'Filter by Status',
        filterByArea: 'Filter by Area',
        allStatus: 'All Status',
        allAreas: 'All Areas',
        planned: 'Planned',
        inProgress: 'In Progress',
        withMentor: 'With Mentor',
        followUp: 'Follow-up',
        statusCompleted: 'Completed',
        archived: 'Archived',
        noGoalsFound: 'No goals found',
        status: 'Status',
        goalObjective: 'Goal',
        lifeArea: 'Life Area',
        currentState: 'Current State',
        futureGoal: 'Future State',
        budget: 'Budget',
        responsible: 'Responsible',
        whoCanHelp: 'Who?',
        howToDo: 'How?',
        startDate: 'When?',
        targetDate: 'Target Date',
        observations: 'Observations',
        measures: 'Measures',
        actions: 'Actions'
      }
    };
    return labels?.[language] || labels?.pt;
  };

  const labels = getLabels();

  const statusOptions = [
    { value: '', label: labels?.allStatus },
    { value: 'planejado', label: labels?.planned },
    { value: 'em_progresso', label: labels?.inProgress },
    { value: 'com_mentoria', label: labels?.withMentor },
    { value: 'acompanhamento', label: labels?.followUp },
    { value: 'concluido', label: labels?.statusCompleted },
    { value: 'arquivado', label: labels?.archived }
  ];

  const areaOptions = [
    { value: '', label: labels?.allAreas },
    { value: 'saude', label: language === 'en' ? 'Health' : 'Saúde' },
    { value: 'financas', label: language === 'en' ? 'Finance' : 'Finanças' },
    { value: 'carreira', label: language === 'en' ? 'Career' : 'Carreira' },
    { value: 'relacionamentos', label: language === 'en' ? 'Relationships' : 'Relacionamentos' },
    { value: 'pessoal', label: language === 'en' ? 'Personal Development' : 'Desenvolvimento Pessoal' },
    { value: 'familia', label: language === 'en' ? 'Family' : 'Família' },
    { value: 'lazer', label: language === 'en' ? 'Leisure' : 'Lazer' },
    { value: 'espiritualidade', label: language === 'en' ? 'Spirituality' : 'Espiritualidade' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      planejado: 'bg-blue-100 text-blue-800 border-blue-200',
      em_progresso: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      com_mentoria: 'bg-purple-100 text-purple-800 border-purple-200',
      acompanhamento: 'bg-orange-100 text-orange-800 border-orange-200',
      concluido: 'bg-green-100 text-green-800 border-green-200',
      arquivado: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      planejado: labels?.planned,
      em_progresso: labels?.inProgress,
      com_mentoria: labels?.withMentor,
      acompanhamento: labels?.followUp,
      concluido: labels?.statusCompleted,
      arquivado: labels?.archived
    };
    return statusMap?.[status] || status;
  };

  const getStatusSelectOptions = () => [
    { value: 'planejado', label: labels?.planned },
    { value: 'em_progresso', label: labels?.inProgress },
    { value: 'com_mentoria', label: labels?.withMentor },
    { value: 'acompanhamento', label: labels?.followUp },
    { value: 'concluido', label: labels?.statusCompleted },
    { value: 'arquivado', label: labels?.archived }
  ];

  const getStatusDropdownColor = (status) => {
    const colors = {
      planejado: 'bg-blue-50 border-blue-200 text-blue-800',
      em_progresso: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      com_mentoria: 'bg-purple-50 border-purple-200 text-purple-800',
      acompanhamento: 'bg-orange-50 border-orange-200 text-orange-800',
      concluido: 'bg-green-50 border-green-200 text-green-800',
      arquivado: 'bg-gray-50 border-gray-200 text-gray-800'
    };
    return colors?.[status] || 'bg-gray-50 border-gray-200 text-gray-800';
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    const numericValue = parseFloat(value) || 0;
    return new Intl.NumberFormat(language === 'pt' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency: language === 'pt' ? 'BRL' : 'USD',
      minimumFractionDigits: 2,
    })?.format(numericValue);
  };

  const filteredAndSortedGoals = useMemo(() => {
    let filtered = goals?.filter(goal => {
      const matchesSearch = goal?.titulo?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          goal?.responsavel?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesStatus = !statusFilter || goal?.status === statusFilter;
      const matchesArea = !areaFilter || goal?.areaVida === areaFilter;
      
      return matchesSearch && matchesStatus && matchesArea;
    }) || [];

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        const aValue = a?.[sortConfig?.key] || '';
        const bValue = b?.[sortConfig?.key] || '';
        
        if (sortConfig?.direction === 'asc') {
          return aValue?.localeCompare(bValue);
        }
        return bValue?.localeCompare(aValue);
      });
    }

    return filtered;
  }, [goals, searchTerm, statusFilter, areaFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig?.key === key && prevConfig?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleStatusChange = (goalId, newStatus) => {
    onStatusChange(goalId, newStatus);
  };



  const columns = [
    { key: 'status', label: labels?.status, sortable: false },
    { key: 'titulo', label: labels?.goalObjective, sortable: true },
    { key: 'responsavel', label: labels?.responsible, sortable: true },
    { key: 'areaVida', label: labels?.lifeArea, sortable: true },
    { key: 'estadoAtual', label: labels?.currentState, sortable: false },
    { key: 'metaFutura', label: labels?.futureGoal, sortable: false },
    { key: 'medidas', label: labels?.measures, sortable: false },
    { key: 'orcamento', label: labels?.budget, sortable: true },
    { key: 'quemPodeAjudar', label: labels?.whoCanHelp, sortable: false },
    { key: 'comoSeraFeito', label: labels?.howToDo, sortable: false },
    { key: 'dataInicio', label: labels?.startDate, sortable: true },
    { key: 'dataAlvo', label: labels?.targetDate, sortable: true },
    { key: 'actions', label: labels?.actions, sortable: false }
  ];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder={labels?.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            iconName="Search"
          />
        </div>
        <div className="flex gap-4">
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder={labels?.filterByStatus}
          />
          <Select
            options={areaOptions}
            value={areaFilter}
            onChange={setAreaFilter}
            placeholder={labels?.filterByArea}
          />
        </div>
      </div>

      {/* Table - Mobile Responsive */}
      <div className="overflow-x-auto">
        <table className="w-full border border-border rounded-lg min-w-[1200px] lg:min-w-[1400px] xl:min-w-[1600px]">
          <thead className="bg-muted">
            <tr>
              {columns?.map(column => (
                <th 
                  key={column?.key}
                  className={`p-2 sm:p-4 text-left font-semibold text-foreground border-b border-border text-xs sm:text-sm ${
                    column?.sortable ? 'cursor-pointer hover:bg-muted-hover transition-colors' : ''
                  }`}
                  onClick={column?.sortable ? () => handleSort(column?.key) : undefined}
                >
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span>{column?.label}</span>
                    {column?.sortable && (
                      <Icon 
                        name={
                          sortConfig?.key === column?.key 
                            ? sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown' :'ChevronsUpDown'
                        } 
                        size={14}
                        className="text-muted-foreground"
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedGoals?.length === 0 ? (
              <tr>
                <td colSpan={columns?.length} className="p-6 sm:p-8 text-center text-muted-foreground">
                  <div className="flex flex-col items-center space-y-2">
                    <Icon name="Target" size={48} className="text-muted-foreground/50" />
                    <p className="text-sm sm:text-base">{labels?.noGoalsFound}</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredAndSortedGoals?.map(goal => (
                <tr key={goal?.id} className="hover:bg-muted/50 transition-colors border-b border-border">
                  {/* Enhanced Status Column with Dropdown */}
                  <td className="p-2 sm:p-4">
                    <div className="relative">
                      <select
                        value={goal?.status}
                        onChange={(e) => handleStatusChange(goal?.id, e?.target?.value)}
                        className={`text-xs sm:text-sm font-medium border rounded-full px-2 sm:px-3 py-1 cursor-pointer 
                                   focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200
                                   hover:shadow-sm ${getStatusDropdownColor(goal?.status)}`}
                        style={{ minWidth: '120px' }}
                      >
                        {getStatusSelectOptions()?.map(option => (
                          <option key={option?.value} value={option?.value}>
                            {option?.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  
                  <td className="p-2 sm:p-4 font-medium text-xs sm:text-sm">
                    <div className="flex items-center justify-between">
                      <div className="max-w-[120px] sm:max-w-[150px] truncate" title={goal?.titulo}>
                        {goal?.titulo}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'titulo')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1"
                        title={language === 'en' ? 'Edit Title' : 'Editar Título'}
                      />
                    </div>
                  </td>
                  
                  <td className="p-2 sm:p-4 text-xs sm:text-sm">
                    <div className="flex items-center justify-between">
                      <div className="max-w-[80px] sm:max-w-[120px] truncate" title={goal?.responsavel}>
                        {goal?.responsavel || '-'}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'responsavel')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1"
                        title={language === 'en' ? 'Edit Responsible' : 'Editar Responsável'}
                      />
                    </div>
                  </td>
                  
                  <td className="p-2 sm:p-4 text-xs sm:text-sm">
                    <div className="flex items-center justify-between">
                      <span className="px-1 sm:px-2 py-1 bg-secondary/20 text-secondary-foreground rounded-full text-xs">
                        {areaOptions?.find(opt => opt?.value === goal?.areaVida)?.label || goal?.areaVida}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'areaVida')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1"
                        title={language === 'en' ? 'Edit Area' : 'Editar Área'}
                      />
                    </div>
                  </td>
                  
                  <td className="p-2 sm:p-4 text-xs sm:text-sm max-w-[120px] sm:max-w-xs">
                    <div className="flex items-center justify-between">
                      <div className="truncate flex-1" title={goal?.estadoAtual}>
                        {goal?.estadoAtual?.length > 30 ? 
                          `${goal?.estadoAtual?.substring(0, 30)}...` : 
                          goal?.estadoAtual || '-'
                        }
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'estadoAtual')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1 flex-shrink-0"
                        title={language === 'en' ? 'Edit Current State' : 'Editar Estado Atual'}
                      />
                    </div>
                  </td>
                  
                  <td className="p-2 sm:p-4 text-xs sm:text-sm max-w-[120px] sm:max-w-xs">
                    <div className="flex items-center justify-between">
                      <div className="truncate flex-1" title={goal?.metaFutura}>
                        {goal?.metaFutura?.length > 30 ? 
                          `${goal?.metaFutura?.substring(0, 30)}...` : 
                          goal?.metaFutura || '-'
                        }
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'metaFutura')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1 flex-shrink-0"
                        title={language === 'en' ? 'Edit Future Goal' : 'Editar Meta Futura'}
                      />
                    </div>
                  </td>
                  
                  {/* Nova coluna Medidas */}
                  <td className="p-2 sm:p-4 text-xs sm:text-sm max-w-[120px] sm:max-w-xs">
                    <div className="flex items-center justify-between">
                      <div className="truncate flex-1" title={goal?.medidas}>
                        {goal?.medidas?.length > 30 ? 
                          `${goal?.medidas?.substring(0, 30)}...` : 
                          goal?.medidas || '-'
                        }
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'medidas')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1 flex-shrink-0"
                        title={language === 'en' ? 'Edit Measures' : 'Editar Medidas'}
                      />
                    </div>
                  </td>
                  
                  <td className="p-2 sm:p-4 text-xs sm:text-sm font-medium">
                    <div className="flex items-center justify-between">
                      <span className="flex-1">
                        {goal?.orcamento ? formatCurrency(goal?.orcamento) : '-'}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'orcamento')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1 flex-shrink-0"
                        title={language === 'en' ? 'Edit Budget' : 'Editar Orçamento'}
                      />
                    </div>
                  </td>
                  
                  <td className="p-2 sm:p-4 text-xs sm:text-sm max-w-[120px] sm:max-w-xs">
                    <div className="flex items-center justify-between">
                      <div className="truncate flex-1" title={goal?.quemPodeAjudar}>
                        {goal?.quemPodeAjudar || '-'}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'quemPodeAjudar')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1 flex-shrink-0"
                        title={language === 'en' ? 'Edit Who Can Help' : 'Editar Quem Pode Ajudar'}
                      />
                    </div>
                  </td>
                  
                  <td className="p-2 sm:p-4 text-xs sm:text-sm max-w-[120px] sm:max-w-xs">
                    <div className="flex items-center justify-between">
                      <div className="truncate flex-1" title={goal?.comoSeraFeito}>
                        {goal?.comoSeraFeito?.length > 30 ? 
                          `${goal?.comoSeraFeito?.substring(0, 30)}...` : 
                          goal?.comoSeraFeito || '-'
                        }
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'comoSeraFeito')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1 flex-shrink-0"
                        title={language === 'en' ? 'Edit How To Do' : 'Editar Como Será Feito'}
                      />
                    </div>
                  </td>
                  
                  <td className="p-2 sm:p-4 text-xs sm:text-sm">
                    <div className="flex items-center justify-between">
                      <span className="flex-1">
                        {goal?.dataInicio && goal.dataInicio !== 'null' && goal.dataInicio !== '' && goal.dataInicio !== null
                          ? new Date(goal.dataInicio + 'T00:00:00').toLocaleDateString(language === 'pt' ? 'pt-BR' : 'en-US')
                          : '-'
                        }
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'dataInicio')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1 flex-shrink-0"
                        title={language === 'en' ? 'Edit Start Date' : 'Editar Data de Início'}
                      />
                    </div>
                  </td>
                  
                  <td className="p-4 text-xs sm:text-sm">
                    <div className="flex items-center justify-between">
                      <span className="flex-1">
                        {goal?.dataAlvo && goal.dataAlvo !== 'null' && goal.dataAlvo !== '' && goal.dataAlvo !== null
                          ? new Date(goal.dataAlvo + 'T00:00:00').toLocaleDateString(language === 'pt' ? 'pt-BR' : 'en-US')
                          : '-'
                        }
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal, 'dataAlvo')}
                        className="hover:bg-blue-100 h-6 w-6 p-0 ml-1 flex-shrink-0"
                        title={language === 'en' ? 'Edit Target Date' : 'Editar Data Alvo'}
                      />
                    </div>
                  </td>
                  
                  {/* Mobile-Friendly Action Buttons */}
                  <td className="p-2 sm:p-4">
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => onView(goal)}
                        className="hover:bg-blue-100 h-7 w-7 sm:h-8 sm:w-8 p-0"
                        title={language === 'en' ? 'View' : 'Visualizar'}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(goal)}
                        className="hover:bg-yellow-100 h-7 w-7 sm:h-8 sm:w-8 p-0"
                        title={language === 'en' ? 'Edit' : 'Editar'}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => onDelete(goal?.id)}
                        className="hover:bg-red-100 text-red-600 h-7 w-7 sm:h-8 sm:w-8 p-0"
                        title={language === 'en' ? 'Delete' : 'Excluir'}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>


    </div>
  );
};

export default GridView;